import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type ThemeMode = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

interface ThemeValue {
  /** What the user chose — including "system", which isn't a look, it's a rule. */
  mode: ThemeMode;
  /** What that actually resolves to right now. Always concrete. */
  resolved: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
  /** Flips between light and dark, resolving "system" to its opposite first. */
  toggle: () => void;
}

const STORAGE_KEY = "lastman:theme";
const ThemeContext = createContext<ThemeValue | null>(null);

function readStoredMode(): ThemeMode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === "light" || raw === "dark" || raw === "system") return raw;
  } catch {
    // Private browsing can throw on access — fall through to the default.
  }
  return "system";
}

function systemPrefersDark(): boolean {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/**
 * Owns the light/dark choice and keeps `<html data-theme>` in step with it.
 *
 * The initial attribute is set by an inline script in index.html, *before* first paint — this
 * provider then takes over. Without that script React would mount a frame later than the
 * browser's first paint, and every dark-mode user would see a white flash on every load.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() =>
    typeof window === "undefined" ? "system" : readStoredMode(),
  );
  const [systemDark, setSystemDark] = useState(() =>
    typeof window === "undefined" ? true : systemPrefersDark(),
  );

  const resolved: ResolvedTheme = mode === "system" ? (systemDark ? "dark" : "light") : mode;

  // Follow the OS live, so a system-mode user switching at sunset updates without a reload.
  useEffect(() => {
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = resolved;

    // Keeps the mobile browser chrome (address bar, notch area) matching the app.
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (meta) meta.content = resolved === "dark" ? "#070b10" : "#f4f7fa";
  }, [resolved]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Non-persisted is still fine for this session.
    }
  }, []);

  const value = useMemo<ThemeValue>(
    () => ({
      mode,
      resolved,
      setMode,
      toggle: () => setMode(resolved === "dark" ? "light" : "dark"),
    }),
    [mode, resolved, setMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used inside ThemeProvider.");
  return context;
}
